'use client';

import React from 'react';
import type { ReactNode } from 'react';
import type { SelectedPersonalization } from './types';
import type { LeadGenerationInteractionProps } from 'components/analytics/types';
import { useGtmTrack } from 'components/analytics/index';
import { ANALYTICS_EVENTS, LEAD_GENERATION_INTERACTION_TYPES } from 'components/analytics/types';
import { convertObjectToArray } from 'components/analytics/utils';
import {
  AnalyticsModuleTrackingProvider,
  useAnalyticsModuleTracking,
} from 'components/analytics/AnalyticsModuleTracking';
import { getVariantDisplayName } from './getVariantDisplayName';
import { StatsigInlineTracker } from './StatsigInlineTracker';

interface ExposureTrackerProps {
  /** Experiment selection for this module, if it participates in an experiment */
  exposure?: SelectedPersonalization;
  /**
   * CSP nonce from request headers. Forwarded to the inline `experiment_bucketed`
   * script-tag tracker so it passes CSP and survives Meta in-app browser hydration
   * failures.
   */
  nonce?: string;
  /**
   * Whether the request has consented to Performance cookies (OneTrust C0002).
   * Read once per request in `getPageContext` and threaded down. When false,
   * the Statsig inline tracker (Hawker `experiment_bucketed` event + POST to
   * `/api/statsig-exposure`) is not rendered at all. The route handler and
   * `logStatsigExposure` still gate server-side as defense-in-depth.
   *
   * Defaults to `true` (opt-in) so callers that haven't been threaded through
   * `getPageContext` don't silently break analytics; the server-side route and
   * SDK gates remain the privacy enforcement of last resort.
   */
  hasPerformanceConsent?: boolean;
  children: ReactNode;
}

const DefaultExposureInner: React.FC<{
  exposure: SelectedPersonalization;
  nonce?: string;
  hasPerformanceConsent: boolean;
}> = ({ exposure, nonce, hasPerformanceConsent }) => {
  const hasTracked = React.useRef(false);
  const { attributes } = useAnalyticsModuleTracking();

  const exposureData = React.useMemo(
    () => ({
      experiment_id: exposure.experienceId,
      variant_index: exposure.variantIndex,
      variant_id: exposure.variantId ?? '',
      variant_name: getVariantDisplayName(exposure.variantIndex),
    }),
    [exposure],
  );

  const analyticsProps: LeadGenerationInteractionProps = React.useMemo(
    () => ({
      interaction_type: LEAD_GENERATION_INTERACTION_TYPES.EXPERIMENT_VIEWED,
      interaction_details: {
        location: attributes.moduleLocation,
        action_detail: 'experience_viewed',
      },
      additional_data: convertObjectToArray(exposureData),
    }),
    [exposureData, attributes.moduleLocation],
  );

  const trackEvent = useGtmTrack(
    {
      eventName: ANALYTICS_EVENTS.LEAD_GENERATION_INTERACTION,
      properties: analyticsProps,
    },
    false,
  );

  React.useEffect(() => {
    if (!hasTracked.current) {
      trackEvent();
      hasTracked.current = true;
    }
  }, [trackEvent]);

  // Inline <script> fires the Hawker `experiment_bucketed` event AND posts to
  // /api/statsig-exposure pre-hydration so they survive Meta in-app browser
  // hydration failures. Skipped for non-Statsig fallthrough since those calls
  // are Statsig-specific.
  if (exposure.vendor !== 'statsig') {
    return null;
  }
  // Skip rendering the inline tracker entirely when the user lacks
  // Performance consent — no Hawker event, no POST, no payload on the wire.
  if (!hasPerformanceConsent) {
    return null;
  }
  // Prefer the readable Statsig group name ('control' / 'treatment-1' / …)
  // over the Contentful variant entry id so experiment_bucketed aligns with
  // the Statsig dashboard's source of truth.
  //
  // `additionalExposures` fires the inner Autotune exposure for the wrapper
  // pattern: the primary tracker logs the outer wrapping experiment (every
  // arm), and each extra tracker logs the bandit for users in the autotune arm
  // so it receives the exposures it needs to learn.
  return (
    <>
      <StatsigInlineTracker
        experimentKey={exposure.experienceId}
        variantKey={exposure.variantName ?? exposure.variantId ?? ''}
        nonce={nonce}
      />
      {exposure.additionalExposures?.map(extra => (
        <StatsigInlineTracker
          key={extra.experienceId}
          experimentKey={extra.experienceId}
          variantKey={extra.variantName}
          nonce={nonce}
        />
      ))}
    </>
  );
};

// Fires exposure events once on mount, not on viewport entry.
export const ExposureTracker: React.FC<ExposureTrackerProps> = ({
  exposure,
  nonce,
  hasPerformanceConsent = true,
  children,
}) => {
  if (!exposure) return <>{children}</>;

  return (
    <AnalyticsModuleTrackingProvider>
      <DefaultExposureInner exposure={exposure} nonce={nonce} hasPerformanceConsent={hasPerformanceConsent} />
      {children}
    </AnalyticsModuleTrackingProvider>
  );
};
